import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GARAGE_LANDING_HEADER_LINKS } from "../../config/storefrontNavTabs";
import "./ListYourGarage.css";

const PAGE_TITLE = "SparePe — List Your Garage | Get 10X More Customers Free";

const TICKER_SPANS = [
  "🔧 FREE FOREVER FOR GARAGES — NO HIDDEN CHARGES",
  "📈 GARAGES ON SPARPE GET 10X MORE WALK-INS",
  "⭐ VERIFIED REVIEWS BUILD TRUST & BOOKINGS",
  "📅 DIGITAL JOB CARDS + ONLINE BOOKINGS INCLUDED",
  "🚀 PHASE 1 CITIES OPEN — LIMITED SPOTS",
];

const SCROLL_STATS = [
  { num: "90%", text: "Owners Search Online First" },
  { num: "10X", text: "More Customers" },
  { num: "₹0", text: "Listing Cost" },
  { num: "24hrs", text: "Go Live" },
  { num: "4.8★", text: "Avg Garage Rating" },
  { num: "127+", text: "Bookings/Month" },
];

const FEATURES = [
  {
    icon: "🌐",
    title: "Online Presence & Discovery",
    desc: "Your garage appears when car owners search for services in your area. Google, SparePe app, everywhere. Stop being invisible.",
    tag: "FREE",
    tagBg: "rgba(16,185,129,0.12)",
    tagColor: "#34D399",
  },
  {
    icon: "📅",
    title: "24/7 Online Bookings",
    desc: "Accept service bookings while you sleep. Customers book from their phone, you get notified instantly. No missed customers ever again.",
    tag: "FREE",
    tagBg: "rgba(99,102,241,0.12)",
    tagColor: "#818CF8",
  },
  {
    icon: "📋",
    title: "Digital Job Cards",
    desc: "Create, track, and share professional job cards. Customers get real-time updates. No more paper mess. Looks professional, builds trust.",
    tag: "FREE",
    tagBg: "rgba(6,182,212,0.12)",
    tagColor: "#22D3EE",
  },
  {
    icon: "⭐",
    title: "Verified Reviews & Ratings",
    desc: "Every completed job can be rated. 5-star garages get 4X more bookings. Your reputation grows automatically with every happy customer.",
    tag: "TRUST",
    tagBg: "rgba(245,158,11,0.12)",
    tagColor: "#FBBF24",
  },
  {
    icon: "📊",
    title: "Business Dashboard",
    desc: "See bookings, revenue trends, peak hours, top services, and customer retention data. Make smarter business decisions backed by real data.",
    tag: "INSIGHTS",
    tagBg: "rgba(139,92,246,0.12)",
    tagColor: "#A78BFA",
  },
  {
    icon: "🛒",
    title: "Spare Parts at Wholesale",
    desc: "Order genuine spare parts from 200+ brands directly within the app. Lower costs = higher margins on every job you do.",
    tag: "EXCLUSIVE",
    tagBg: "rgba(236,72,153,0.12)",
    tagColor: "#F472B6",
  },
];

const SERVICES = [
  "🔧 General Service",
  "🛢️ Oil Change",
  "🛑 Brakes",
  "⚡ Electrical",
  "❄️ AC Service",
  "🎨 Denting/Paint",
  "🛞 Tyre Service",
  "🔋 Battery",
  "🔍 Diagnostics",
  "💧 Car Wash",
  "🏗️ Engine Work",
  "📱 Accessories",
];

const CITIES = [
  { name: "Delhi NCR", spots: 31, status: "ok" },
  { name: "Mumbai", spots: 24, status: "med" },
  { name: "Bangalore", spots: 9, status: "low" },
  { name: "Hyderabad", spots: 38, status: "ok" },
  { name: "Chennai", spots: 42, status: "ok" },
  { name: "Pune", spots: 15, status: "med" },
  { name: "Kolkata", spots: 45, status: "ok" },
  { name: "Ahmedabad", spots: 33, status: "ok" },
];

const STORIES = [
  {
    quote:
      "I was skeptical about listing online. But within 2 weeks of going live on SparePe, I got 14 new customers who found me through the app. Best decision I made this year.",
    name: "Vikram Singh",
    role: "Owner, Singh Auto Care, Delhi",
    avatar: "🔧",
    avatarBg: "rgba(16,185,129,0.15)",
    metric: "+14 new customers in Week 2",
    metricBg: "rgba(16,185,129,0.12)",
    metricColor: "#34D399",
  },
  {
    quote:
      "The digital job card feature alone is worth it. My customers feel like they're dealing with a professional workshop. Reviews have been amazing — 4.9 stars after 50 jobs.",
    name: "Priya Malhotra",
    role: "Owner, AutoCare Express, Mumbai",
    avatar: "⭐",
    avatarBg: "rgba(245,158,11,0.15)",
    metric: "4.9★ rating — 50+ reviews",
    metricBg: "rgba(245,158,11,0.12)",
    metricColor: "#FBBF24",
  },
  {
    quote:
      "I used to spend ₹5,000/month on pamphlets. Now I get more customers for free through SparePe than I ever got from those pamphlets. Online presence is everything now.",
    name: "Deepak Yadav",
    role: "Owner, SpeedFix Garage, Bangalore",
    avatar: "📈",
    avatarBg: "rgba(99,102,241,0.15)",
    metric: "₹0 marketing cost now",
    metricBg: "rgba(99,102,241,0.12)",
    metricColor: "#818CF8",
  },
  {
    quote:
      "The ordering spare parts through SparePe at wholesale prices saves me ₹8,000-12,000 every month. That alone makes it worth listing. Everything else is a bonus.",
    name: "Raju Kumar",
    role: "Mechanic, Kumar Motors, Hyderabad",
    avatar: "💰",
    avatarBg: "rgba(255,51,102,0.15)",
    metric: "Saves ₹10K/month on parts",
    metricBg: "rgba(255,51,102,0.12)",
    metricColor: "#FF6688",
  },
];

const COMPARE_BEFORE = [
  { icon: "😶", text: "Invisible online — zero search presence" },
  { icon: "📞", text: "Rely only on phone calls and walk-ins" },
  { icon: "📄", text: "Paper job cards — messy, loseable" },
  { icon: "🤷", text: "No customer reviews = no trust signal" },
  { icon: "💸", text: "Losing ₹15K+ per day to online competitors" },
  { icon: "📊", text: "Zero insights on your business performance" },
  { icon: "😰", text: "Hoping customers will somehow find you" },
];

const COMPARE_AFTER = [
  { icon: "🌐", text: "Top of search — car owners find you first" },
  { icon: "📅", text: "24/7 online bookings — never miss a customer" },
  { icon: "📱", text: "Digital job cards — professional & sharable" },
  { icon: "⭐", text: "Verified reviews — 4X more trust from buyers" },
  { icon: "📈", text: "10X more customers from online discovery" },
  { icon: "📊", text: "Real-time dashboard — revenue, trends, insights" },
  { icon: "🚀", text: "Customers actively seeking you out online" },
];

function GarageGrowthVfx() {
  return (
    <section className="lyg-vfx-section" aria-hidden="true">
      <div className="lyg-vfx-card">
        <div className="lyg-vfx-inner">
          <h3 className="lyg-vfx-title">
            🏎️ Garage <span className="lyg-vfx-green">Growth</span> Dashboard
          </h3>
          <p className="lyg-vfx-sub">
            Bookings, revenue, and ratings — all climbing. See what happens when you go digital.
          </p>
          <div className="lyg-vfx-dials">
            <div className="lyg-vfx-dial lyg-fill-bookings">
              <span>↑82%</span>
            </div>
            <div className="lyg-vfx-dial lyg-fill-revenue">
              <span>+₹2.4L</span>
            </div>
            <div className="lyg-vfx-dial lyg-fill-rating">
              <span>4.9★</span>
            </div>
          </div>
          <div className="lyg-vfx-labels">
            <span>Bookings</span>
            <span>Revenue</span>
            <span>Rating</span>
          </div>
        </div>
      </div>
    </section>
  );
}

const ListYourGarage = () => {
  const navigate = useNavigate();
  const rootRef = useRef(null);
  const [toast, setToast] = useState({ msg: "", show: false });
  const [selectedServices, setSelectedServices] = useState(() => new Set());

  useEffect(() => {
    const prev = document.title;
    document.title = PAGE_TITLE;
    const prevScroll = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = "smooth";
    return () => {
      document.title = prev;
      document.documentElement.style.scrollBehavior = prevScroll;
    };
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const els = root.querySelectorAll(".lyg-sol-card, .lyg-prob-card, .lyg-earn-card, .lyg-story-card");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("lyg-visible");
          }
        });
      },
      { threshold: 0.1 },
    );
    els.forEach((el) => {
      el.classList.add("lyg-scroll-reveal");
      observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollToReg = useCallback(() => {
    document.getElementById("reg-section")?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (window.location.hash === "#reg-section") {
      const timer = window.setTimeout(() => scrollToReg(), 100);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [scrollToReg]);

  const scrollToProblem = useCallback(() => {
    document.getElementById("problem-section")?.scrollIntoView({ behavior: "smooth" });
  }, []);

  const toggleService = useCallback((label) => {
    setSelectedServices((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  }, []);

  const showToast = useCallback((msg) => {
    setToast({ msg, show: true });
    window.setTimeout(() => setToast((t) => ({ ...t, show: false })), 5000);
  }, []);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      const fd = new FormData(e.currentTarget);
      const name = fd.get("g-name")?.toString().trim() || "Your garage";
      showToast(
        `🎉 "${name}" has been submitted! Our team will call you within 24 hours to complete the listing.`,
      );
      e.currentTarget.reset();
      setSelectedServices(new Set());
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    [showToast],
  );

  const tickerDup = [...TICKER_SPANS, ...TICKER_SPANS];
  const scrollDup = [...SCROLL_STATS, ...SCROLL_STATS];

  return (
    <div className="lyg-root lyg-bg-glow" ref={rootRef}>
      <div className="lyg-ticker-wrap">
        <div className="lyg-ticker">
          {tickerDup.map((text, i) => (
            <span key={`${text}-${i}`}>{text}</span>
          ))}
        </div>
      </div>

      <header className="lyg-header">
        <div className="lyg-header-inner">
          <div className="lyg-logo">SparePe</div>
          <nav className="lyg-header-nav" aria-label="Marketing">
            {GARAGE_LANDING_HEADER_LINKS.map((link) => (
              <button
                key={link.id}
                type="button"
                className={link.id === "list-garage" ? "active" : undefined}
                onClick={() => {
                  if (link.scrollOnSamePage) {
                    scrollToReg();
                    return;
                  }
                  const path = link.href.split("#")[0];
                  navigate(path);
                }}
              >
                {link.label}
              </button>
            ))}
          </nav>
          <button type="button" className="lyg-header-cta" onClick={scrollToReg}>
            List Free →
          </button>
        </div>
      </header>

      <section className="lyg-hero">
        <div className="lyg-container lyg-hero-content">
          <div className="lyg-urgency-pill">
            <span className="lyg-dot" />
            Free Listing — Only During Launch Phase
          </div>
          <h1>
            Your Garage is Great.
            <br />
            But Nobody Can <span className="lyg-hot">Find It</span> Online.
            <br />
            <span className="lyg-grad">Let&apos;s Fix That.</span>
          </h1>
          <p className="lyg-hero-sub">
            90% of car owners <strong>search online before choosing a garage.</strong> If you&apos;re not there,
            you&apos;re losing customers to competitors who are. SparePe makes your garage visible to{" "}
            <strong>every car owner in your city</strong> — completely free.
          </p>
          <p className="lyg-hero-hook">🆓 Free forever. No charges. No commission. No catch.</p>
          <div className="lyg-hero-cta-wrap">
            <button type="button" className="lyg-btn-mega lyg-primary" onClick={scrollToReg}>
              🔧 List My Garage — Free Forever
            </button>
            <button type="button" className="lyg-btn-mega lyg-ghost" onClick={scrollToProblem}>
              📊 See What You&apos;re Missing
            </button>
          </div>
          <div className="lyg-proof-strip">
            <div className="lyg-proof-item">
              🆓 <span className="lyg-val">100%</span> Free forever
            </div>
            <div className="lyg-proof-item">
              ⚡ Live in <span className="lyg-val">24 hours</span>
            </div>
            <div className="lyg-proof-item">
              📅 <span className="lyg-val">Online bookings</span> included
            </div>
          </div>
        </div>
      </section>

      <div className="lyg-container">
        <div className="lyg-scroll-banner" style={{ margin: "0 -20px 64px" }}>
          <div className="lyg-scroll-track">
            {scrollDup.map((row, i) => (
              <div key={`${row.num}-${i}`} className="lyg-scroll-item">
                <span className="lyg-num">{row.num}</span> {row.text}
              </div>
            ))}
          </div>
        </div>

        <div className="lyg-problem-section" id="problem-section">
          <div className="lyg-prob-badge">⚠️ THE PROBLEM</div>
          <div className="lyg-prob-title">
            Every Day You&apos;re Not Online,
            <br />
            <span style={{ color: "var(--lyg-danger)" }}>You&apos;re Losing ₹15,000+ in Revenue</span>
          </div>
          <div className="lyg-prob-desc">
            Your competitors are already on Google, Instagram, and auto platforms. Vehicle owners are choosing them —
            not because they&apos;re better, but because they&apos;re{" "}
            <strong style={{ color: "var(--lyg-text)" }}>findable.</strong> You&apos;re invisible to 90% of potential
            customers.
          </div>
          <div className="lyg-prob-grid">
            <div className="lyg-prob-card">
              <div className="lyg-prob-card-icon">🔍</div>
              <div className="lyg-prob-card-stat lyg-red">90%</div>
              <div className="lyg-prob-card-lbl">Car owners search online before picking a garage</div>
            </div>
            <div className="lyg-prob-card">
              <div className="lyg-prob-card-icon">👻</div>
              <div className="lyg-prob-card-stat lyg-red">0</div>
              <div className="lyg-prob-card-lbl">Your online presence right now (probably)</div>
            </div>
            <div className="lyg-prob-card">
              <div className="lyg-prob-card-icon">💸</div>
              <div className="lyg-prob-card-stat lyg-red">₹4.5L</div>
              <div className="lyg-prob-card-lbl">Revenue lost annually by not being online</div>
            </div>
            <div className="lyg-prob-card">
              <div className="lyg-prob-card-icon">📈</div>
              <div className="lyg-prob-card-stat" style={{ color: "var(--lyg-success)" }}>
                10X
              </div>
              <div className="lyg-prob-card-lbl">Bookings increase with an online listing</div>
            </div>
          </div>
        </div>

        <div className="lyg-compare-section">
          <div className="lyg-compare-header">
            <h2>Without SparePe vs With SparePe</h2>
            <p>The difference is like night and day</p>
          </div>
          <div className="lyg-compare-grid">
            <div className="lyg-compare-col lyg-before">
              <div className="lyg-cc-header" style={{ color: "var(--lyg-danger)" }}>
                ❌ Without SparePe
              </div>
              {COMPARE_BEFORE.map((row) => (
                <div key={row.text} className="lyg-cc-item">
                  <span className="lyg-icon">{row.icon}</span>
                  <span>{row.text}</span>
                </div>
              ))}
            </div>
            <div className="lyg-compare-vs">VS</div>
            <div className="lyg-compare-col lyg-after">
              <div className="lyg-cc-header" style={{ color: "var(--lyg-success)" }}>
                ✅ With SparePe
              </div>
              {COMPARE_AFTER.map((row) => (
                <div key={row.text} className="lyg-cc-item">
                  <span className="lyg-icon">{row.icon}</span>
                  <span>{row.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="lyg-solution-section">
          <div className="lyg-sol-header">
            <h2>
              Everything You Get.
              <br />
              <span
                style={{
                  background: "var(--lyg-grad1)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Absolutely Free.
              </span>
            </h2>
            <p>No premium tier. No feature locks. Every garage gets the full experience.</p>
          </div>
          <div className="lyg-sol-grid">
            {FEATURES.map((f) => (
              <div key={f.title} className="lyg-sol-card">
                <div className="lyg-sol-icon">{f.icon}</div>
                <div className="lyg-sol-title">{f.title}</div>
                <div className="lyg-sol-desc">{f.desc}</div>
                <div className="lyg-sol-tag" style={{ background: f.tagBg, color: f.tagColor }}>
                  {f.tag}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="lyg-earnings-section">
          <div className="lyg-earn-header">
            <h2>
              What{" "}
              <span
                style={{
                  background: "var(--lyg-grad2)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                More Customers
              </span>{" "}
              Means For You
            </h2>
            <p>Conservative estimates based on average garage economics in India</p>
          </div>
          <div className="lyg-earn-cards">
            <div className="lyg-earn-card">
              <div className="lyg-earn-label">Without SparePe</div>
              <div className="lyg-earn-val" style={{ color: "var(--lyg-text-muted)" }}>
                3-5
              </div>
              <div className="lyg-earn-sub">Jobs per day (walk-in only)</div>
              <div className="lyg-earn-detail">
                Revenue: ~₹5,000-8,000/day
                <br />
                Monthly: ~₹1.5L-2.4L
              </div>
            </div>
            <div
              className="lyg-earn-card"
              style={{
                borderColor: "var(--lyg-primary)",
                boxShadow: "0 0 0 1px var(--lyg-primary), 0 16px 40px var(--lyg-primary-glow)",
              }}
            >
              <div className="lyg-earn-label" style={{ color: "var(--lyg-primary)" }}>
                With SparePe
              </div>
              <div
                className="lyg-earn-val"
                style={{
                  background: "var(--lyg-grad1)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                8-15
              </div>
              <div className="lyg-earn-sub">Jobs per day (walk-in + online)</div>
              <div className="lyg-earn-detail">
                Revenue: ~₹12,000-25,000/day
                <br />
                Monthly: ~₹3.6L-7.5L
                <br />
                <strong style={{ color: "var(--lyg-success)" }}>+₹2L to ₹5L extra/month</strong>
              </div>
            </div>
            <div className="lyg-earn-card">
              <div className="lyg-earn-label">After 6 Months</div>
              <div
                className="lyg-earn-val"
                style={{
                  background: "var(--lyg-grad2)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                15-25+
              </div>
              <div className="lyg-earn-sub">Jobs per day (top-rated garage)</div>
              <div className="lyg-earn-detail">
                Revenue: ~₹25,000-50,000/day
                <br />
                Monthly: ~₹7.5L-15L
                <br />
                <strong style={{ color: "var(--lyg-warning)" }}>Reviews compound over time</strong>
              </div>
            </div>
          </div>
        </div>

        <div style={{ margin: "64px 0 24px", textAlign: "center" }}>
          <h2 style={{ fontSize: 34, fontWeight: 900, letterSpacing: "-0.5px", marginBottom: 10 }}>
            What Garage Owners Are Saying
          </h2>
          <p style={{ fontSize: 15, color: "var(--lyg-text-dim)" }}>Early adopters share their experience</p>
        </div>
        <div className="lyg-stories-scroll">
          {STORIES.map((s) => (
            <div key={s.name} className="lyg-story-card">
              <div className="lyg-story-quote">{s.quote}</div>
              <div className="lyg-story-author">
                <div className="lyg-story-avatar" style={{ background: s.avatarBg }}>
                  {s.avatar}
                </div>
                <div>
                  <div className="lyg-story-name">{s.name}</div>
                  <div className="lyg-story-role">{s.role}</div>
                  <div className="lyg-story-metric" style={{ background: s.metricBg, color: s.metricColor }}>
                    {s.metric}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="lyg-fomo-section">
          <div className="lyg-fomo-badge">
            <span
              style={{
                display: "inline-block",
                width: 8,
                height: 8,
                background: "var(--lyg-primary)",
                borderRadius: "50%",
                animation: "lyg-blink 1s infinite",
              }}
            />
            Phase 1 Enrollment Open
          </div>
          <div className="lyg-fomo-title">
            Free Listing Spots Are <span style={{ color: "var(--lyg-danger)" }}>Filling Fast</span>
          </div>
          <div className="lyg-fomo-desc">
            We&apos;re launching city-by-city. Once your city&apos;s spots are filled, new garages go on a waitlist.
            Early-listed garages get <strong style={{ color: "var(--lyg-text)" }}>permanent priority ranking.</strong>
          </div>
          <div className="lyg-city-grid">
            {CITIES.map((c) => (
              <div key={c.name} className="lyg-city-card">
                <div className="lyg-city-name">{c.name}</div>
                <div className={`lyg-city-spots lyg-${c.status}`}>{c.spots} spots left</div>
              </div>
            ))}
          </div>
          <button type="button" className="lyg-btn-mega lyg-primary" style={{ margin: "0 auto" }} onClick={scrollToReg}>
            🔧 List My Garage Now — Free
          </button>
        </div>

        <div className="lyg-reg-section" id="reg-section">
          <div className="lyg-reg-header">
            <h2>🔧 List Your Garage in 3 Minutes</h2>
            <p>No documents needed to start. Add details later. Go live within 24 hours.</p>
            <div className="lyg-micro">🆓 100% Free — No Charges Ever</div>
          </div>
          <form className="lyg-reg-form" onSubmit={handleSubmit}>
            <div className="lyg-form-row">
              <div>
                <label className="lyg-form-label" htmlFor="g-name">
                  Garage Name *
                </label>
                <input
                  type="text"
                  className="lyg-form-input"
                  id="g-name"
                  name="g-name"
                  placeholder="e.g. Sharma Motors Workshop"
                  required
                />
              </div>
              <div>
                <label className="lyg-form-label" htmlFor="g-owner">
                  Owner Name *
                </label>
                <input
                  type="text"
                  className="lyg-form-input"
                  id="g-owner"
                  name="g-owner"
                  placeholder="e.g. Rajinder Sharma"
                  required
                />
              </div>
            </div>
            <div className="lyg-form-row">
              <div>
                <label className="lyg-form-label" htmlFor="g-phone">
                  Phone (WhatsApp) *
                </label>
                <input
                  type="tel"
                  className="lyg-form-input"
                  id="g-phone"
                  name="g-phone"
                  placeholder="+91 98712 34567"
                  required
                />
              </div>
              <div>
                <label className="lyg-form-label" htmlFor="g-city">
                  City *
                </label>
                <input type="text" className="lyg-form-input" id="g-city" name="g-city" placeholder="e.g. New Delhi" required />
              </div>
            </div>
            <div className="lyg-form-row">
              <div>
                <label className="lyg-form-label" htmlFor="g-type">
                  Garage Type
                </label>
                <select className="lyg-form-select lyg-form-input" id="g-type" name="g-type" defaultValue="">
                  <option value="">Select type</option>
                  <option value="multi">Multi-brand Workshop</option>
                  <option value="authorized">Authorized Service Center</option>
                  <option value="specialist">Specialist (AC, Electrical, etc.)</option>
                  <option value="tyre">Tyre & Wheel Shop</option>
                  <option value="body">Denting & Painting</option>
                  <option value="two-wheeler">Two-Wheeler Garage</option>
                </select>
              </div>
              <div>
                <label className="lyg-form-label" htmlFor="g-addr">
                  Address
                </label>
                <input
                  type="text"
                  className="lyg-form-input"
                  id="g-addr"
                  name="g-addr"
                  placeholder="Shop no, area, landmark"
                />
              </div>
            </div>
            <div className="lyg-form-group">
              <span className="lyg-form-label">Services You Offer (tap to select)</span>
              <div className="lyg-svc-grid">
                {SERVICES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className={`lyg-svc-chip${selectedServices.has(s) ? " lyg-selected" : ""}`}
                    onClick={() => toggleService(s)}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div className="lyg-form-check">
              <input type="checkbox" id="g-terms" name="g-terms" required />
              <label htmlFor="g-terms">
                I want to list my garage on SparePe for free. I understand there are no charges, no commission, and I can
                update my profile anytime.
              </label>
            </div>
            <button type="submit" className="lyg-form-submit">
              🔧 List My Garage — Free Forever
            </button>
            <div className="lyg-form-trust">
              <span>🆓 Always Free</span>
              <span>🔒 Data Secured</span>
              <span>⚡ 24hr Go-Live</span>
              <span>❌ Cancel Anytime</span>
            </div>
          </form>
        </div>

        <div className="lyg-final-cta">
          <h2>
            Every Day Without SparePe
            <br />
            is <span style={{ color: "var(--lyg-danger)" }}>Revenue Lost.</span>
          </h2>
          <p>
            Your competitors are already getting online bookings. The garages that list first get the reviews, the
            rankings, and the customers. Don&apos;t let them win.
          </p>
          <button type="button" className="lyg-btn-mega lyg-primary" onClick={scrollToReg}>
            🔧 I&apos;m In — List Free Now
          </button>
        </div>
      </div>

      <GarageGrowthVfx />

      <footer className="lyg-footer">
        <div className="lyg-container">
          <p>© 2026 SparePe Technologies Pvt. Ltd. — Reimagining India&apos;s Auto Parts & Garage Industry</p>
        </div>
      </footer>

      <div className={`lyg-toast${toast.show ? " lyg-show" : ""}`} role="status" aria-live="polite">
        {toast.msg}
      </div>
    </div>
  );
};

export default ListYourGarage;
